import { Button, NumberInput, Alert, SkeletonText, SkeletonRegion } from '@parsina/framework';
import { CT } from '@parsina/framework/tokens';
import { Lock } from 'lucide-react';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import SegmentedControl from '../../components/common/SegmentedControl';
import ErrorState from '@parsina/framework/clinical/src/components/common/ErrorState';
import { FileChip } from '@parsina/framework/clinical/src/components/files/FileRow';
import { RADIUS } from '@parsina/framework/clinical/src/constants/theme';
import { megabytes, fileErrorText } from '@parsina/framework/clinical/src/lib/clinicalFileLimits';
import { TRI_STATES, ceilingMb } from './useUploadSettings';

// «فایل‌ها» — rendered inside the «صفحه ویزیت» tab on both surfaces (§23.3 A8: a section,
// not a tab). The state and its save live in `useUploadSettings`, which the SCREEN owns,
// so the screen's own leave guard knows when there is something to lose.
//
// What it says, in order: the doctor's size limit (tri-state, the MB field disabled —
// not hidden — while the limit is not on, with the platform ceiling under it), whether
// DICOM uploads are on, and, read-only, what actually applies at each clinic: the
// effective limit, whether the clinic locked it, and where the value came from.

const sourceKey = (source) => {
  const s = String(source || '').toLowerCase();
  if (s.startsWith('practitioner')) return 'settings.files.source.practitioner';
  if (s.startsWith('clinic')) return 'settings.files.source.clinic';
  if (s.startsWith('platform') || s === 'uploaders' || s.startsWith('kind')) return 'settings.files.source.platform';
  return null;
};

export default function UploadFilesSettings({ s, compact = false, testid = 'settings-files' }) {
  const { t, has, formatNumber } = useTranslation();

  if (s.status === 'loading') {
    return (
      <SkeletonRegion label={t('common.state.loading')} data-testid={`${testid}-loading`}>
        <SkeletonText lines={4} />
      </SkeletonRegion>
    );
  }
  if (s.status === 'error') {
    return <div data-testid={`${testid}-error`} data-state="error"><ErrorState error={s.error} title={t('settings.files.loadFailed')} onRetry={s.retry} /></div>;
  }

  const ceilings = s.settings?.ceilings;
  const triItems = (field) => TRI_STATES.map((id) => ({ id, label: t(`settings.files.${field}.${id}`) }));
  const clinics = s.settings?.clinics || [];

  return (
    <div data-testid={testid} data-state="content" style={{ display: 'flex', flexDirection: 'column', gap: compact ? '1rem' : '1.25rem' }}>
      <section style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <h3 style={{ margin: 0, fontSize: '0.8125rem', fontWeight: 700, color: CT.ink }}>{t('settings.files.sizeLimit.title')}</h3>
        <p style={{ margin: 0, fontSize: '0.75rem', color: CT.mute, lineHeight: 1.8 }}>{t('settings.files.sizeLimit.hint')}</p>
        <SegmentedControl
          items={triItems('sizeLimit')}
          value={s.form.sizeLimit}
          onChange={(v) => s.setField('sizeLimit', v)}
          size="sm"
          ariaLabel={t('settings.files.sizeLimit.title')}
          testId={`${testid}-size-limit`}
        />
        <div style={{ maxWidth: compact ? 'none' : '16rem' }}>
          <NumberInput
            label={t('settings.files.maxSize')}
            value={s.form.maxMb}
            onChange={(v) => s.setField('maxMb', typeof v === 'string' || typeof v === 'number' ? String(v) : v?.target?.value ?? '')}
            unit={t('settings.files.mb')}
            min={1}
            max={ceilingMb(ceilings)}
            disabled={s.form.sizeLimit !== 'on'}
            error={s.errors.maxMb ? t(s.errors.maxMb, { max: formatNumber(ceilingMb(ceilings)) }) : undefined}
            helperText={t('settings.files.ceiling', { mb: formatNumber(ceilingMb(ceilings)) })}
            data-testid={`${testid}-max-size`}
          />
        </div>
      </section>

      <section style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <h3 style={{ margin: 0, fontSize: '0.8125rem', fontWeight: 700, color: CT.ink }}>{t('settings.files.dicom.title')}</h3>
        <p style={{ margin: 0, fontSize: '0.75rem', color: CT.mute, lineHeight: 1.8 }}>{t('settings.files.dicom.hint')}</p>
        <SegmentedControl
          items={triItems('dicom')}
          value={s.form.dicom}
          onChange={(v) => s.setField('dicom', v)}
          size="sm"
          ariaLabel={t('settings.files.dicom.title')}
          testId={`${testid}-dicom`}
        />
      </section>

      <section data-testid={`${testid}-clinics`} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <h3 style={{ margin: 0, fontSize: '0.8125rem', fontWeight: 700, color: CT.ink }}>{t('settings.files.clinics.title')}</h3>
        {clinics.length === 0 ? (
          <p data-testid={`${testid}-clinics-empty`} style={{ margin: 0, fontSize: '0.75rem', color: CT.mute }}>{t('settings.files.clinics.empty')}</p>
        ) : (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {clinics.map((c) => {
              const src = (c.sources || []).find((x) => x.field === 'maxFileBytes' || x.field === 'max_file_bytes')
                || (c.sources || []).find((x) => x.field === 'sizeLimitEnabled' || x.field === 'size_limit_enabled');
              const srcKey = sourceKey(src?.source);
              const overridden = c.locked && s.form.sizeLimit === 'off' && c.sizeLimitEnabled;
              return (
                <li key={c.clinicId} data-testid={`${testid}-clinic-${c.clinicId}`} data-locked={c.locked || undefined} style={{
                  borderRadius: RADIUS.control, border: `1px solid ${CT.border}`, padding: '0.5rem 0.75rem',
                  display: 'flex', flexDirection: 'column', gap: '0.25rem',
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <span dir="auto" style={{ fontSize: '0.8125rem', fontWeight: 600, color: CT.ink, flex: 1, minWidth: 0 }}>{c.clinicName || t('settings.files.clinics.unnamed')}</span>
                    {c.locked && (
                      <FileChip tone="warn" data-testid={`${testid}-clinic-${c.clinicId}-locked`}>
                        <Lock size={11} aria-hidden /> {t('settings.files.clinics.locked')}
                      </FileChip>
                    )}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: CT.ink2 }} data-testid={`${testid}-clinic-${c.clinicId}-effective`}>
                    {c.sizeLimitEnabled
                      ? t('settings.files.clinics.limit', { mb: formatNumber(megabytes(c.maxFileBytes)) })
                      : t('settings.files.clinics.noLimit', { mb: formatNumber(ceilingMb(ceilings)) })}
                    {srcKey && <span style={{ color: CT.mute }}> · {t(srcKey)}</span>}
                  </span>
                  {overridden && (
                    <span style={{ fontSize: '0.6875rem', color: CT.warnInk }} data-testid={`${testid}-clinic-${c.clinicId}-overridden`}>{t('settings.files.clinics.overridden')}</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {s.saveError && <Alert variant="error" data-testid={`${testid}-save-error`}>{fileErrorText(t, has, s.saveError, 'settings.files.saveFailed')}</Alert>}

      {s.dirty && (
        <div data-testid={`${testid}-savebar`} style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', flexWrap: 'wrap', borderTop: `1px solid ${CT.border}`, paddingTop: '0.75rem' }}>
          <Button variant="outline" onClick={s.reset} disabled={s.saving} className={compact ? 'min-h-11' : undefined} data-testid={`${testid}-reset`}>
            {t('settings.files.discard')}
          </Button>
          <Button onClick={s.save} disabled={s.saving} className={compact ? 'min-h-11' : undefined} data-testid={`${testid}-save`}>
            {s.saving ? t('common.state.saving') : t('common.actions.save')}
          </Button>
        </div>
      )}
    </div>
  );
}
